import { C } from '../../lib/theme';
import { styles } from '../../lib/styles';
import { addCalendarDays, fmtDate, NOTICE_WINDOW_CALENDAR_DAYS } from '../../lib/util';
import type { Case } from '../../domain/initial-cases';
import { Dialog } from '../Dialog';
import { useUIState } from '../../state/contexts/UIStateContext';
import { notificationContact, useLifecycleActions } from '../../state/actions/lifecycle';

// #543 — ONE button, because there is only one notification path.
//
// "Send via API" used to sit beside it and reached the same `sendNotification`
// with a different method string. Nothing delivers an API notification, so it
// recorded the notice, stamped the 30-day deadline, opened the cure window and
// audited NOTIFICATION_SENT while sending nothing. It is gone; see
// NOTIFICATION_METHOD in state/actions/lifecycle.ts for the reasoning and for
// the matching server-side enum that closes the same hole for a non-SPA caller.
export function SendNotificationModal({ c }: { c: Case }) {
  const { setModal } = useUIState();
  const { sendNotification } = useLifecycleActions();
  // The notice goes out by email and nowhere else, so with no address there is
  // nothing to send and recording one would be the #543 bug again. Newly
  // submitted cases cannot get here without an address (the vetting-entry gate
  // refuses), so this is the already-decided backlog.
  //
  // #693 — the recipient is the submission's authorizer contact, billing
  // contact as fallback, resolved by the same rule the API addresses the mail
  // with. Showing WHICH contact matters: an analyst about to start a 30-day
  // clock should see that the notice is going to the billing address because
  // the submission gave no authorizer email, not merely that some address exists.
  const contact = notificationContact(c);
  const canSend = contact !== null;
  return (
    <Dialog title={`Send ${c.decision === 'SUCCESSFUL' ? 'Successful' : 'Unsuccessful'} Vet Notification`} onClose={() => setModal(null)}>
        <div style={{ fontSize: 13, marginBottom: 8 }}>Registrant: <strong>{c.registrant}</strong></div>
        <div style={{ fontSize: 13, marginBottom: 8 }}>
          {contact
            ? <>To: <strong>{contact.name || c.registrant}</strong> ({contact.email})
                {' '}<span style={{ color: C.textMuted }}>
                  — {contact.source === 'authorizer' ? 'authorizer contact' : 'billing contact'}
                </span></>
            : <>To: <strong>no notification contact</strong></>}
        </div>
        <div style={{ fontSize: 13, marginBottom: 8 }}>Decision Date: {fmtDate(c.decisionDate)}</div>
        <div style={{ fontSize: 13, marginBottom: 16 }}>30-Day Deadline: {fmtDate(addCalendarDays(c.decisionDate!, NOTICE_WINDOW_CALENDAR_DAYS))}</div>
        {c.decision === 'UNSUCCESSFUL' && (
          <div style={{ background: C.dangerLight, borderRadius: 12, padding: 12, marginBottom: 16, border: `2px solid ${C.danger}` }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: C.danger, marginBottom: 8 }}>Unsuccessful Vet Notice Content</div>
            <div style={{ fontSize: 12 }}>Registrant: {c.registrant}</div>
            <div style={{ fontSize: 12 }}>Associated TNs: {c.tns.join(', ') || 'None'}</div>
            <div style={{ fontSize: 12 }}>Reason(s): {c.reasonCodes.join('; ')}</div>
            <div style={{ fontSize: 11, color: C.textMuted, marginTop: 8 }}>A 12 business day cure window will open upon sending this notice.</div>
          </div>
        )}
        {!canSend && (
          <div style={{ background: C.dangerLight, borderRadius: 12, padding: 12, marginBottom: 16, border: `2px solid ${C.danger}` }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: C.danger, marginBottom: 4 }}>No notification contact</div>
            <div style={{ fontSize: 12 }}>
              This notice can only be delivered by email, and this case&rsquo;s submission carries neither an
              authorizer email nor a billing email. Fill one in on the Onboarding tab first —
              recording a notification with no address would start the deadline clocks against a notice
              the registrant never receives.
            </div>
          </div>
        )}
        <div style={{ display: 'flex', gap: 12 }}>
          <button
            style={{ ...styles.btn, ...styles.btnGold, ...(canSend ? {} : { opacity: 0.5, cursor: 'not-allowed' }) }}
            disabled={!canSend}
            onClick={() => sendNotification(c.id)}
          >Send via Email</button>
          <button style={{ ...styles.btn, ...styles.btnOutline }} onClick={() => setModal(null)}>Cancel</button>
        </div>
    </Dialog>
  );
}
